/**
 * Ticket KN1: Die Keynote-Seite.
 *
 * Anders als bei Kursen und Mentorings gibt es nur diese eine Seite, keine
 * Detailseiten: Überschrift, Einleitung, Sprungpunkte zu den Referenten, die
 * Referenten untereinander, zum Schluss das Anfrageformular.
 *
 * Solange der Funktionsschalter `keynotes_visible` zu ist, liefert die
 * Leseschicht Besuchern nichts – die Seite antwortet dann mit 404. Angemeldete
 * interne Rollen sehen sie. Es gibt keinen Prüfschlüssel in der Adresse.
 *
 * Kein Preis auf dieser Seite, an keiner Stelle. Kein Zeeg-Kalender und kein
 * Erstgespräch: Die erste Frage ist hier der Termin.
 */

import { createFileRoute, notFound } from "@tanstack/react-router";
import { Layout } from "@/components/Layout";
import { NotFoundPage } from "@/components/NotFoundPage";
import { SpeechBubbleAnimation } from "@/components/politisch-kommunizieren/SpeechBubbleAnimation";

import { KeynoteSpeakerSection } from "@/components/keynotes/KeynoteSpeakerSection";
import { KeynoteRequestForm } from "@/components/keynotes/KeynoteRequestForm";
import { loadKeynotePage } from "@/lib/keynotes.functions";
import { keynoteHeadMeta } from "@/lib/keynotes-meta";
import { KEYNOTE_PAGE_PATH } from "@/lib/keynotes";
import { jsonLdScript, keynoteSpeakersJsonLd, siteUrl } from "@/lib/structured-data";

export const KEYNOTE_PAGE_HEADING = "Keynotes und Vorträge";
export const KEYNOTE_PAGE_INTRO_PARAGRAPHS = [
  "Wir sprechen dort, wo politische Kommunikation gerade das Thema ist: auf Verbandstagen, in kommunalen Klausuren, bei Fachveranstaltungen und in Fraktionsklausuren.",
  "Einen festen Vortragskatalog finden Sie hier nicht. Wer einen Redner bucht, hat einen Anlass und selten einen Titel. Sagen Sie uns, worum es bei Ihnen geht, und wir schneiden das Thema darauf zu.",
  
] as const;
export const KEYNOTE_PAGE_INTRO = KEYNOTE_PAGE_INTRO_PARAGRAPHS[0];

export const Route = createFileRoute("/wissen/keynotes")({
  loader: async () => {
    const { visible, speakers, shareImageUrl } = await loadKeynotePage();
    // Geschlossener Schalter und keine interne Rolle: die Seite existiert für
    // Besucher nicht. Fehlende Inhalte sind dagegen kein 404 – interne Rollen
    // sehen dann den Hinweis unten.
    if (!visible) throw notFound();
    return { speakers, shareImageUrl };
  },
  head: ({ loaderData }) => ({
    meta: keynoteHeadMeta(loaderData?.shareImageUrl ?? null),
    links: [{ rel: "canonical", href: siteUrl(KEYNOTE_PAGE_PATH) }],
    scripts: loaderData ? jsonLdScript(keynoteSpeakersJsonLd(loaderData.speakers)) : [],
  }),
  notFoundComponent: () => <NotFoundPage />,
  errorComponent: () => <NotFoundPage />,
  component: KeynotesPage,
});

function KeynotesPage() {
  const { speakers } = Route.useLoaderData();

  return (
    <Layout>
      <section>
        <div className="section-y mx-auto w-full max-w-[1400px] px-6 md:px-12">
          <div className="grid gap-12 md:grid-cols-2 md:items-center">
            <div>
              <h1 className="heading-h1">{KEYNOTE_PAGE_HEADING}</h1>
              <div className="mt-6 max-w-3xl space-y-4">
                {KEYNOTE_PAGE_INTRO_PARAGRAPHS.map((paragraph) => (
                  <p key={paragraph} className="prose-lead text-muted-foreground">
                    {paragraph}
                  </p>
                ))}
              </div>
            </div>
            <div className="flex w-full items-center justify-center">
              <SpeechBubbleAnimation />
            </div>
          </div>

          {speakers.length === 0 ? (
            <p className="mt-16 max-w-3xl rounded border border-dashed border-foreground/25 p-6 font-body text-base text-muted-foreground">
              Es sind noch keine Referentinnen und Referenten freigegeben. Sobald im internen
              Bereich unter „Keynotes“ Personen und Themen auf „veröffentlicht“ stehen, erscheinen
              sie hier.
            </p>
          ) : (
            <div className="mt-16 space-y-16 lg:mt-20 lg:space-y-20">
              {speakers.map((speaker) => (
                <KeynoteSpeakerSection key={speaker.id} speaker={speaker} />
              ))}
            </div>
          )}

          <div className="mt-20 border-t border-foreground/15 pt-16 lg:mt-24">
            <KeynoteRequestForm speakers={speakers} />
          </div>
        </div>
      </section>
    </Layout>
  );
}
